<template>
  <div class="order-audit">
    <!-- 页面标题 -->
    <div class="page-header">
      <el-button icon="el-icon-back" size="small" @click="goBack">返回</el-button>
      <h2>订单审核</h2>
      <span class="order-no" @click="refreshData">订单编号：{{ orderInfo.orderNo || '--' }}</span>
      <el-tag :type="statusTagType(orderInfo.status)" size="medium">
        {{ orderInfo.statusName || '--' }}
      </el-tag>
    </div>

    <!-- 基本信息 -->
    <el-card class="base-info" shadow="never">
      <div slot="header">
        <span>基本信息</span>
      </div>
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="info-item">
            <span class="label">订单类型：</span>
            <span>{{ orderInfo.orderType || '--' }}</span>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="info-item">
            <span class="label">订单状态：</span>
            <el-tag :type="statusTagType(orderInfo.status)">{{ orderInfo.statusName || '--' }}</el-tag>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="info-item">
            <span class="label">
              <i class="el-icon-truck"></i> 车牌号：
            </span>
            <span>{{ orderInfo.vehiclePlateNumber || '--' }}</span>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="info-item">
            <span class="label">司机：</span>
            <span>{{ orderInfo.driverName || '--' }}</span>
          </div>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="info-item">
            <span class="label">创建人：</span>
            <span>{{ orderInfo.createBy || '--' }}</span>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="info-item">
            <span class="label">创建时间：</span>
            <span>{{ orderInfo.createTime || '--' }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <span class="label">备注：</span>
            <span>{{ orderInfo.remark || '无' }}</span>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- 按 carrierNo 分组展示明细，每组带审核按钮 -->
    <el-card class="detail-group" shadow="never" v-for="(group, index) in groupedDetails" :key="index">
      <div slot="header" class="group-header">
        <div class="group-title">
          <span class="carrier-no">运单号：{{ group.carrierNo }}</span>
          <el-tag size="small" type="info">共 {{ group.items.length }} 项</el-tag>
          <el-tag 
            size="small" 
            :type="group.auditStatus === 'passed' ? 'success' : group.auditStatus === 'rejected' ? 'danger' : 'warning'">
            {{ group.auditStatus === 'passed' ? '已通过' : group.auditStatus === 'rejected' ? '已驳回' : '待审核' }}
          </el-tag>
          <el-tag 
            size="small" 
            type="info"
            v-if="group.remark">
            有备注
          </el-tag>
        </div>
        <div class="group-stats">
          <span>总数量：<b>{{ group.totalQuantity }}</b> {{ group.items[0] ? group.items[0].unit || '万支' : '万支' }}</span>
          <span style="margin-left: 20px;">总金额：<b style="color: #f56c6c;">¥{{ group.totalPrice.toFixed(2) }}</b></span>
        </div>
      </div>

      <!-- 运单备注（只读展示） -->
      <div class="group-remark" v-if="group.remark">
        <span class="remark-label">📝 运单备注：</span>
        <span class="remark-text">{{ group.remark }}</span>
      </div>

      <el-table :data="group.items" border style="width: 100%" size="small">
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="contractNo" label="合同号" min-width="120" align="center" />
        <el-table-column prop="productName" label="产品名称" min-width="150" align="center" />
        <el-table-column prop="quantity" label="数量" width="100" align="center">
          <template slot-scope="scope">
            {{ scope.row.quantity }} {{ scope.row.unit }}
          </template>
        </el-table-column>
        <el-table-column prop="unitPrice" label="单价" width="100" align="center">
          <template slot-scope="scope">
            ¥{{ scope.row.unitPrice }}
          </template>
        </el-table-column>
        <el-table-column prop="totalPrice" label="小计" width="120" align="center">
          <template slot-scope="scope">
            ¥{{ scope.row.totalPrice.toFixed(2) }}
          </template>
        </el-table-column>
        <el-table-column prop="netWeight" label="净重(kg)" width="100" align="center" />
        <el-table-column prop="distance" label="距离(km)" width="100" align="center" />
        <el-table-column prop="originWarehouseName" label="线路" min-width="150" align="center" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.originWarehouseName }} - {{ scope.row.destinationWarehouseName }}
          </template>
        </el-table-column>
        <el-table-column prop="supplierName" label="供应商" min-width="150" align="center" show-overflow-tooltip />
        <el-table-column prop="planShipDate" label="计划发货日期" width="120" align="center">
          <template slot-scope="scope">
            {{ scope.row.planShipDate }} - {{ scope.row.planArrivalDate }}
          </template>
        </el-table-column>
        <el-table-column prop="statusName" label="明细状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="statusTagType(scope.row.status)" size="small">{{ scope.row.statusName }}</el-tag>
          </template>
        </el-table-column>
      </el-table>

      <!-- 每组独立的审核操作按钮 -->
      <div class="group-actions">
        <el-button 
          type="primary" 
          size="small" 
          @click="handleGroupAuditPass(group, index)"
          :loading="group.submitLoading"
          :disabled="group.auditStatus === 'passed' || group.auditStatus === 'rejected'">
          审核通过
        </el-button>
        <el-button 
          type="danger" 
          size="small" 
          @click="handleGroupAuditReject(group, index)"
          :loading="group.submitLoading"
          :disabled="group.auditStatus === 'passed' || group.auditStatus === 'rejected'">
          驳回
        </el-button>
        <span v-if="group.auditStatus === 'passed'" style="color: #67c23a; margin-left: 10px;">
          <i class="el-icon-check"></i> 已审核通过
        </span>
        <span v-if="group.auditStatus === 'rejected'" style="color: #f56c6c; margin-left: 10px;">
          <i class="el-icon-close"></i> 已驳回
        </span>
      </div>
    </el-card>
  </div>
</template>

<script>
import api from '../utils/api'

export default {
  name: 'OrderAudit',
  data() {
    return {
      orderId: null,
      carrierNo: null,
      orderInfo: {},
      detailList: [],
      allSubmitLoading: false,
      submitLoading: false
    }
  },
  computed: {
    groupedDetails() {
      if (!this.detailList || this.detailList.length === 0) return []

      const groupMap = new Map()

      this.detailList.forEach(item => {
        const key = item.carrierNo || '未知运单'
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            carrierNo: key,
            items: [],
            totalQuantity: 0,
            totalPrice: 0,
            auditStatus: null,
            submitLoading: false,
            remark: null
          })
        }
        const group = groupMap.get(key)
        group.items.push(item)
        group.totalQuantity += item.quantity || 0
        group.totalPrice += item.totalPrice || 0
        
        // 如果明细有备注且组还没有备注，取明细的备注
        if (item.remark && !group.remark) {
          group.remark = item.remark
        }
      })

      return Array.from(groupMap.values())
    },
    allAuditDisabled() {
      if (!this.groupedDetails.length) return true
      return this.groupedDetails.every(g => g.auditStatus === 'passed' || g.auditStatus === 'rejected')
    }
  },
  created() {
    console.log('=== OrderAudit created ===')
    this.orderId = this.$route.query.id
    this.carrierNo = this.$route.query.carrierNo || ''
    console.log('订单ID:', this.orderId)
    console.log('承运单号:', this.carrierNo)
    if (this.orderId) {
      this.fetchDetail()
    } else {
      this.$message.warning('缺少订单ID参数')
    }
  },
  methods: {
    async refreshData() {
      await this.fetchDetail()
    },
    async fetchDetail() {
      try {
        console.log('开始调用接口...')
        const res = await api.get('/api/Order/' + this.orderId, {
            carrierNo: this.carrierNo || ''
          }
        )
        console.log('接口返回:', res)
        const data = res.data || res
        this.orderInfo = data
        this.detailList = data.orderDetails || []
        console.log('解析数据完成:', this.detailList.length, '条明细')
      } catch (error) {
        console.error('获取订单详情失败:', error)
        this.$message.error('获取订单详情失败，请重试')
      }
    },
    async fetchAllDetail() {
      try {
        console.log('开始调用接口...')
        const res = await api.get('/api/Order/' + this.orderId
        )
        console.log('接口返回:', res)
        const data = res.data || res
        this.orderInfo = data
        this.detailList = data.orderDetails || []
        console.log('解析数据完成:', this.detailList.length, '条明细')
      } catch (error) {
        console.error('获取订单详情失败:', error)
        this.$message.error('获取订单详情失败，请重试')
      }
    },    
    statusTagType(status) {
      const map = {
        0: 'info',
        1: 'warning',
        2: 'primary',
        3: 'success',
        4: 'danger',
        5: 'success',
        6: 'info',
        7: 'success',
        8: 'success',
        9: 'danger'
      }
      return map[status] || 'info'
    },
    goBack() {
      this.$router.go(-1)
    },

    /**
     * 单个承运单审核通过
     */
    async handleGroupAuditPass(group, index) {
      try {
        await this.$confirm(
          `确认审核通过运单号 "${group.carrierNo}" 吗？`, 
          '提示', 
          { type: 'warning' }
        )
        
        this.$set(group, 'submitLoading', true)
        const carrierNos=(group.carrierNo || '')
                        .split(/[,，\s]+/)
                        .map(s => s.trim())
                        .filter(s => s)
        await api.put('/api/Order/ByCarrierNo/update-status', { 
          orderId: this.orderId, 
          carrierNo: carrierNos, 
          Action: 0,
          Remark: '用户操作审核'
        })
        
        this.$set(group, 'auditStatus', 'passed')
        this.$message.success(`运单号 "${group.carrierNo}" 审核通过成功`)
        await this.refreshData()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('审核通过失败:', error)
          this.$message.error('审核操作失败，请重试')
        }
      } finally {
        this.$set(group, 'submitLoading', false)
      }
    },

    /**
     * 单个承运单驳回
     */
    async handleGroupAuditReject(group, index) {
      try {
        await this.$confirm(
          `确认驳回运单号 "${group.carrierNo}" 吗？`, 
          '提示', 
          { type: 'warning' }
        )
        const carrierNos=group.carrierNo
                         .split(/[,，\s]+/)
                        .map(s => s.trim())
                        .filter(s => s)
        this.$set(group, 'submitLoading', true)
        
        await api.put('/api/Order/ByCarrierNo/update-status', { 
          orderId: this.orderId, 
          carrierNo: carrierNos, 
          Action: 1,
          Remark: '用户操作驳回'
        })
        
        this.$set(group, 'auditStatus', 'rejected')
        this.$message.success(`运单号 "${group.carrierNo}" 驳回成功`)
        await this.refreshData()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('驳回失败:', error)
          this.$message.error('驳回操作失败，请重试')
        }
      } finally {
        this.$set(group, 'submitLoading', false)
      }
    },

    /**
     * 全部审核通过
     */
    async handleAllAuditPass() {
      const pendingGroups = this.groupedDetails.filter(g => !g.auditStatus)
      if (!pendingGroups.length) {
        this.$message.warning('所有承运单已审核')
        return
      }
      
      try {
        await this.$confirm(
          `确认全部审核通过（共 ${pendingGroups.length} 个承运单）吗？`, 
          '提示', 
          { type: 'warning' }
        )
        
        this.allSubmitLoading = true
        
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        pendingGroups.forEach(g => {
          this.$set(g, 'auditStatus', 'passed')
        })
        
        this.$message.success('全部审核通过成功')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('全部审核通过失败:', error)
          this.$message.error('操作失败，请重试')
        }
      } finally {
        this.allSubmitLoading = false
      }
    },

    /**
     * 全部驳回
     */
    async handleAllAuditReject() {
      const pendingGroups = this.groupedDetails.filter(g => !g.auditStatus)
      if (!pendingGroups.length) {
        this.$message.warning('所有承运单已审核')
        return
      }
      
      try {
        await this.$confirm(
          `确认全部驳回（共 ${pendingGroups.length} 个承运单）吗？`, 
          '提示', 
          { type: 'warning' }
        )
        
        this.allSubmitLoading = true
        
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        pendingGroups.forEach(g => {
          this.$set(g, 'auditStatus', 'rejected')
        })
        
        this.$message.success('全部驳回成功')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('全部驳回失败:', error)
          this.$message.error('操作失败，请重试')
        }
      } finally {
        this.allSubmitLoading = false
      }
    }
  }
}
</script>

<style scoped>
.order-audit {
  padding: 20px;
  background: #f0f2f5;
  min-height: 100vh;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  background: #fff;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.page-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.page-header .order-no {
  color: #909399;
  font-size: 14px;
  margin-left: auto;
}

.order-audit .el-card {
  margin-bottom: 20px;
  border-radius: 8px;
}

.order-audit .el-card /deep/ .el-card__header {
  background: #fafafa;
  border-bottom: 1px solid #e8ecf1;
  font-weight: 600;
  font-size: 15px;
  padding: 12px 20px;
}

.order-audit .el-card /deep/ .el-card__body {
  padding: 20px;
}

.base-info .info-item {
  padding: 6px 0;
  font-size: 14px;
}

.base-info .info-item .label {
  color: #909399;
  display: inline-block;
  min-width: 70px;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 8px;
}

.group-header .group-title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.group-header .group-title .carrier-no {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.group-header .group-stats {
  font-size: 14px;
  color: #606266;
}

.group-header .group-stats b {
  font-size: 16px;
}

/* 运单备注样式 */
.group-remark {
  padding: 10px 14px;
  margin-bottom: 14px;
  background: #f5f7fa;
  border-radius: 4px;
  border-left: 3px solid #409eff;
  display: flex;
  align-items: flex-start;
}

.group-remark .remark-label {
  font-weight: 600;
  color: #303133;
  margin-right: 8px;
  white-space: nowrap;
}

.group-remark .remark-text {
  color: #606266;
  word-break: break-all;
  flex: 1;
}

.group-actions {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e4e7ed;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.action-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.action-footer .el-button {
  min-width: 100px;
}
</style>